import { mark } from 'regenerator-runtime';
import iconSVG from 'url:../../img/icons.svg';

export default class view {
  _data;

  /**
   * Renders the received data to the DOM.
   *
   * If the provided data is falsy or an empty array, it renders an error message instead.
   * Otherwise, it generates the markup and inserts it into the parent element after clearing previous content.
   *
   * @param {Object|Array} data - The data to render (e.g., an object or an array of objects).
   */
  render(data) {
    if (!data || (Array.isArray(data) && data.length === 0))
      return this.renderError();

    this._data = data;
    const markup = this._generateMarkup();
    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }

  /**
   * Efficiently updates the DOM with new data without re-rendering the entire view.
   *
   * This method compares the newly generated markup with the current DOM elements
   * and updates only the changed text content and attributes.
   * If the number of elements differs, it falls back to a full render.
   *
   * @param {Object|Array} data - The new data to be used for updating the view.
   */
  update(data) {
    this._data = data;
    // Generate Markup with new data
    const markup = this._generateMarkup();

    // create new markup elements
    const newElements = Array.from(
      document
        .createRange()
        .createContextualFragment(markup)
        .querySelectorAll('*')
    );

    // get current markup elements
    const curElemens = Array.from(this._parentElement.querySelectorAll('*'));

    if (curElemens.length !== newElements.length) return this.render(data);
    // compare new and current elements
    newElements.forEach(function (newEl, i) {
      const curEl = curElemens[i];

      if (!newEl.isEqualNode(curEl)) {
        // check if element have textcontent for ignoring parent elements
        if (newEl.firstChild?.nodeValue.trim()) {
          // update new text
          curEl.textContent = newEl.textContent;
        }

        // update new attributes
        Array.from(newEl.attributes).forEach(attr =>
          curEl.setAttribute(attr.name, attr.value)
        );
      }
    });
  }

  /**
   * Clears all inner HTML content of the parent element.
   *
   * This method removes all child nodes and HTML content from
   * the element referenced by `this._parentElement`.
   *
   * @private
   */
  _clear() {
    this._parentElement.innerHTML = '';
  }

  /**
   * Clear inner Html of parent element and render
   * Loading spinner in parent element
   */
  renderSpinner() {
    const markup = `
              <div class="spinner">
                <svg>
                  <use href="${iconSVG}#icon-loader"></use>
                </svg>
              </div>`;

    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }

  /**
   *    Renders an error message to the parent element.
   *
   * This method generates an HTML markup for an error message,
   * clears the current content of the parent element,
   * and inserts the error markup at the beginning.
   *
   * @param {string} [err=this._errorMessage] - error message you want to render in parent element
   */
  renderError(err = this._errorMessage) {
    const markup = `
              <div class="error">
                <div>
                  <svg>
                    <use href="${iconSVG}#icon-alert-triangle"></use>
                  </svg>
                </div>
                <p>${err}</p>
              </div>
              `;

    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }

  /**
   * Renders an error message to the parent element.
   *
   * This method generates an HTML markup for an error message,
   * clears the current content of the parent element,
   * and inserts the error markup at the beginning.
   *
   * @param {string} [message = this._message] - message you want to render in parent element
   */

  renderMessage(message = this._message) {
    const markup = `
                <div class="message">
                  <div>
                    <svg>
                      <use href="src/img/icons.svg#icon-smile"></use>
                    </svg>
                  </div>
                  <p>
                    ${message}
                  </p>
                </div>
              `;

    this._clear();
    this._parentElement.insertAdjacentHTML('afterbegin', markup);
  }
}
